import { useEffect, useRef, useState } from "react";

interface ShipmentIdFilterProps {
  /** Current shipment ID value (comma-separated string for multiple) */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Callback when filter is removed */
  onRemove: () => void;
  /** Whether to open immediately */
  initialOpen?: boolean;
}

/**
 * Validate and parse shipment IDs from input string
 * @returns { valid: boolean, ids: number[], error?: string }
 */
function parseShipmentIds(input: string): {
  valid: boolean;
  ids: number[];
  error?: string;
} {
  if (!input.trim()) {
    return { valid: true, ids: [] };
  }

  const parts = input.split(",").map((s) => s.trim());
  const ids: number[] = [];

  for (const part of parts) {
    if (!part) continue;

    const num = parseInt(part, 10);
    if (isNaN(num) || num <= 0) {
      return {
        valid: false,
        ids: [],
        error: `Invalid shipment ID: "${part}" - must be a positive number`,
      };
    }
    ids.push(num);
  }

  return { valid: true, ids };
}

/**
 * ShipmentIdFilter component - Input field for filtering by Shipment ID
 *
 * Supports:
 * - Single shipment ID: "12345"
 * - Multiple comma-separated IDs: "12345, 67890, 11111"
 *
 * Validates that all IDs are positive integers.
 */
export function ShipmentIdFilter({
  value,
  onChange,
  onRemove,
  initialOpen = false,
}: ShipmentIdFilterProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // React to initialOpen prop changes (for auto-open on add)
  useEffect(() => {
    if (initialOpen) {
      setIsOpen(true);
    }
  }, [initialOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Inline apply logic to avoid dependency issues
        const result = parseShipmentIds(inputValue);
        if (result.valid) {
          setError(null);
          setIsOpen(false);
          onChange(inputValue);
        } else {
          setError(result.error || "Invalid input");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, inputValue, onChange]);

  const applyValue = () => {
    const result = parseShipmentIds(inputValue);
    if (result.valid) {
      setError(null);
      setIsOpen(false);
      onChange(inputValue);
    } else {
      setError(result.error || "Invalid input");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      applyValue();
    } else if (event.key === "Escape") {
      setIsOpen(false);
      setInputValue(value); // Reset to original value
      setError(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    // Clear error on input change
    if (error) setError(null);
  };

  // Format display value
  const getDisplayValue = () => {
    if (!value) return "All";
    const result = parseShipmentIds(value);
    if (result.ids.length === 0) return "All";
    if (result.ids.length === 1) return result.ids[0].toString();
    return `${result.ids.length} IDs`;
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Chip button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-1.5 pr-7
          text-xs
          ${isOpen ? "bg-blue-100 dark:bg-blue-900/30" : "bg-white dark:bg-[#232f48]"}
          border border-gray-200 dark:border-[#324467]
          rounded-full
          hover:bg-gray-50 dark:hover:bg-gray-700
          transition-colors duration-150
        `}
      >
        <span className="font-semibold text-gray-600 dark:text-gray-300">Shipment ID:</span>
        <span className="text-gray-600 dark:text-gray-300">{getDisplayValue()}</span>
      </button>

      {/* Remove button */}
      <button
        type="button"
        aria-label="Remove Shipment ID filter"
        title="Remove Shipment ID filter"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={`
          absolute right-1.5 top-1/2 -translate-y-1/2 p-0
          text-gray-400 hover:text-gray-600
          dark:text-gray-500 dark:hover:text-gray-300
          transition-colors duration-150
        `}
      >
        <span className="material-symbols-outlined text-sm">close</span>
      </button>

      {/* Dropdown input */}
      {isOpen && (
        <div
          className={`
            absolute left-0 top-full mt-1 z-50
            w-72 p-3
            bg-white dark:bg-[#192233]
            border border-gray-200 dark:border-[#324467]
            rounded-lg shadow-lg
          `}
        >
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Shipment ID(s)
          </label>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="e.g. 12345 or 12345, 67890"
            className={`
              w-full px-3 py-2
              text-sm text-gray-900 dark:text-white
              bg-white dark:bg-[#111722]
              border rounded-md
              focus:outline-none focus:ring-2 focus:ring-primary/50
              ${error ? "border-red-500 focus:border-red-500" : "border-gray-300 dark:border-[#324467] focus:border-primary"}
            `}
          />
          {error ? (
            <p className="mt-1 text-[10px] text-red-500">{error}</p>
          ) : (
            <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
              Separate multiple IDs with commas
            </p>
          )}
        </div>
      )}
    </div>
  );
}
